import { hash } from '@ember/helper';
import { on } from '@ember/modifier';
/* eslint-disable no-unsafe-optional-chaining */
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';
import {
  PrismCard,
  PrismInput,
  PrismSelectInput,
  PrismTable,
} from '@mylighthouse/prism-ember';
import { task } from 'ember-concurrency';
import { t } from 'ember-intl';
import { not, or } from 'ember-truth-helpers';
import {
  MAPPING_TYPE,
  MAPPING_TYPE_TRANSLATIONS,
} from 'frontend-utils/utils/constants/benchmark-insight';
import GeneralFormFieldsCancelAndConfirm from 'lumen/components/general/form-fields/cancel-and-confirm';
import SettingsMappingTableCellCategoryDropdownBenchmark from 'lumen/components/settings/mapping/table/cell-category-dropdown-benchmark';
import SettingsMappingTableCellCheckboxMappingCellCheckboxMappingBenchmark from 'lumen/components/settings/mapping/table/cell-checkbox-mapping/cell-checkbox-mapping-benchmark';
import SettingsMappingTableCellHeaderCheckboxMapping from 'lumen/components/settings/mapping/table/cell-header-checkbox-mapping';
import SettingsMappingTableCellHeaderRoomNights from 'lumen/components/settings/mapping/table/cell-header-room-nights';
import SettingsMappingTableCellPmsValueCellPmsValueBenchmark from 'lumen/components/settings/mapping/table/cell-pms-value/cell-pms-value-benchmark';
import SettingsMappingTableCellRoomNights from 'lumen/components/settings/mapping/table/cell-room-nights';
import {
  DISMISSED_SUGGESTIONS_LOCALSTORAGE_KEY,
  PMS_MAPPING_CATEGORY_FILTERS,
  PMS_MAPPING_TABS_TRANSLATION_KEY_MAPPING,
  SELECT_ALL_CHECKBOX_STATES,
} from 'lumen/utils/constants/pms-mapping-constants';
import { timeout } from 'lumen/utils/waiters';

// The maximum amount of rows that the user can bulk update.
const BULK_UPDATE_MAX_ROWS = 3000;

interface SettingsMappingTableTableMappingBenchmarkSignature {
  Args: {
    breakdown: unknown;
    cancelMappingChanges: unknown;
    isDisabled: unknown;
    isEditing: unknown;
    isMappingDisabled: unknown;
    patchMapping: unknown;
    rootCategory: unknown;
    rows: unknown;
    startEditing: unknown;
    stopEditing: unknown;
  };
}

export default class SettingsMappingTableTableMappingBenchmark extends Component<SettingsMappingTableTableMappingBenchmarkSignature> {
  @service declare alerting: Services['alerting'];
  @service('benchmark-insight/beni-service')
  declare benchmarkInsightBeniService: Services['benchmark-insight/beni-service'];
  @service declare errorTracking: Services['error-tracking'];
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare intl: Services['intl'];
  @service declare localStorage: Services['local-storage'];

  // @ts-expect-error: Incorrect type
  @tracked currentFilter = this.defaultFilter;
  @tracked initialSorts = [
    {
      valuePath: 'pmsValue',
      ascending: true,
    },
  ];
  @tracked isViewingUnmappedOnly = false;
  @tracked pageNumber = 1;
  @tracked pmsMappingValuesToPatch = [];
  @tracked searchText = '';
  @tracked showOnlyUnsavedChanges = false;
  @tracked suggestionIsSelected = undefined;

  defaultFilter = {
    id: PMS_MAPPING_CATEGORY_FILTERS.ALL,
    // @ts-expect-error: Incorrect type
    label: this.intl.t('settings.pms_mapping.filter_label.all'),
  };

  filterRegex = /[^a-zA-Z]+/g;

  // @ts-expect-error: Incorrect type
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
  originalPmsMappingValues = this.args.rows.map(({ categoryId, pmsValue }) => {
    return {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      categoryId,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      pmsValue,
    };
  });

  debouncedSearchTask = task({ restartable: true }, async () => {
    await timeout();
  });

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get categoryOptions() {
    let categories = [];

    if (this.args.rootCategory) {
      categories = this.createCategories(this.args.rootCategory);
    }

    // Add unmapped option because users should be able to unmap pms mapping values.
    categories.push({
      label: this.intl.t('settings.pms_mapping.unmapped'),
      value: this.intl.t('settings.pms_mapping.unmapped'),
      id: null,
    });

    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return categories;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get columns() {
    const {
      selectAllCheckboxState,
      mappingDisabledTooltipText,
      disableRowActions,
      // eslint-disable-next-line @typescript-eslint/unbound-method
      setRowSelection,
    } = this;
    const { isDisabled, isMappingDisabled } = this.args;

    const columnSelect = {
      name: null,
      valuePath: 'isSelected',
      isSortable: false,
      width: 32, // Checkbox 16px, padding left 16px
      thComponent: SettingsMappingTableCellHeaderCheckboxMapping,
      tdComponent:
        SettingsMappingTableCellCheckboxMappingCellCheckboxMappingBenchmark,
      // eslint-disable-next-line @typescript-eslint/unbound-method
      selectAll: this.selectAll,
      maxRowsUpdateCount: BULK_UPDATE_MAX_ROWS,
      selectAllCheckboxState,
      isDisabled,
      isMappingDisabled,
      mappingDisabledTooltipText,
      setRowSelection,
    };

    const columnPmsValue = {
      name: this.intl.t(
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        PMS_MAPPING_TABS_TRANSLATION_KEY_MAPPING[this.args.breakdown],
      ),
      valuePath: 'pmsValue',
      width: 100,
      tdComponent: SettingsMappingTableCellPmsValueCellPmsValueBenchmark,
      isRateView: this.isRateView,
      categoryOptions: this.categoryOptions,
      // eslint-disable-next-line @typescript-eslint/unbound-method
      onChangeCategory: this.onChangeCategory,
      disableRowActions,
    };

    const columnSegment = {
      name: this.intl.t('settings.pms_mapping.segment'),
      valuePath: 'categoryId',
      width: 200,
      tdComponent: SettingsMappingTableCellCategoryDropdownBenchmark,
      categoryOptions: this.categoryOptions,
      // eslint-disable-next-line @typescript-eslint/unbound-method
      onChangeCategory: this.onChangeCategory,
      disableRowActions,
      isMappingDisabled,
    };

    const columnRoomNights = {
      name: this.intl.t('settings.pms_mapping.room_nights'),
      valuePath: 'roomNights',
      width: 100,
      thComponent: SettingsMappingTableCellHeaderRoomNights,
      // Use a cell component to format the room revenue using the hotelAndMarketService.formatRate formatter.
      tdComponent: SettingsMappingTableCellRoomNights,
    };

    return [columnSelect, columnPmsValue, columnSegment, columnRoomNights];
  }

  // Disable row actions (e.g. dropdowns in the rows) if any rows are selected.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get disableRowActions() {
    return Boolean(this.selectedRows.length);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get filteredList() {
    // @ts-expect-error: Incorrect type
    let result = [...this.args?.rows];
    switch (this.currentFilter?.id) {
      case PMS_MAPPING_CATEGORY_FILTERS.MAPPED:
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        result = this.listMapped;
        break;
      case PMS_MAPPING_CATEGORY_FILTERS.UNMAPPED:
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        result = this.listUnmapped;
        break;
      case PMS_MAPPING_CATEGORY_FILTERS.SUGGESTED_MAPPING:
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        result = this.listSuggestedMapping;
        break;
      case PMS_MAPPING_CATEGORY_FILTERS.UNSAVED_CHANGES:
        result = this.listUnsavedChanges;
        break;
      default:
        break;
    }
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return result;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get filterOptions() {
    const options = this.isRateView
      ? this.filtersLists.rate
      : this.filtersLists.market;
    return options;
  }

  // Preserve reference for isEqual check in PowerSelect code
  @cached
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get filtersLists() {
    return {
      market: [
        {
          id: PMS_MAPPING_CATEGORY_FILTERS.ALL,
          label: this.intl.t('settings.pms_mapping.filter_label.all'),
        },
        {
          id: PMS_MAPPING_CATEGORY_FILTERS.MAPPED,
          label: this.intl.t('settings.pms_mapping.filter_label.mapped'),
        },
        {
          id: PMS_MAPPING_CATEGORY_FILTERS.UNMAPPED,
          label: this.intl.t('settings.pms_mapping.filter_label.unmapped'),
        },
        {
          id: PMS_MAPPING_CATEGORY_FILTERS.UNSAVED_CHANGES,
          label: this.intl.t(
            'settings.pms_mapping.filter_label.unsaved_changes',
          ),
          disabled: this.unsavedOptionDisabled,
        },
      ],
      rate: [
        {
          id: PMS_MAPPING_CATEGORY_FILTERS.ALL,
          label: this.intl.t('settings.pms_mapping.filter_label.all'),
        },
        {
          id: PMS_MAPPING_CATEGORY_FILTERS.MAPPED,
          label: this.intl.t('settings.pms_mapping.filter_label.mapped'),
        },
        {
          id: PMS_MAPPING_CATEGORY_FILTERS.SUGGESTED_MAPPING,
          label: this.intl.t(
            'settings.pms_mapping.filter_label.suggested_mapping',
          ),
        },
        {
          id: PMS_MAPPING_CATEGORY_FILTERS.UNMAPPED,
          label: this.intl.t('settings.pms_mapping.filter_label.unmapped'),
        },
        {
          id: PMS_MAPPING_CATEGORY_FILTERS.UNSAVED_CHANGES,
          label: this.intl.t(
            'settings.pms_mapping.filter_label.unsaved_changes',
          ),
          disabled: this.unsavedOptionDisabled,
        },
      ],
    };
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hotel() {
    return this.hotelAndMarketService.hotel;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isRateView() {
    return this.args.breakdown === MAPPING_TYPE.RATE_CODE;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get listMapped() {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
    return this.args.rows.filter((row) => row.categoryId !== null);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get listSuggestedMapping() {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-return
    return this.args.rows.filter(
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      (row) => row.suggestedCategoryId !== null && row.readOnly !== true,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get listUnmapped() {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-return
    return this.args.rows.filter(
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      (row) => row.categoryId === null && row.suggestedCategoryId === null,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get listUnsavedChanges() {
    return this.pmsMappingValuesToPatch;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get mappingDisabledTooltipText() {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    return this.intl.t(MAPPING_TYPE_TRANSLATIONS[this.args.breakdown]);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get rows() {
    const rowsToShow = this.rowsFiltered;

    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return rowsToShow;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get rowsFiltered() {
    if (this.args?.rows) {
      let rowsFiltered = this.filteredList;

      // Filter on unsaved changes only
      if (this.showOnlyUnsavedChanges) {
        rowsFiltered = rowsFiltered.filter((row) => {
          return this.pmsMappingValuesToPatch.some(
            // @ts-expect-error: Incorrect type
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            (pmsMappingValue) => pmsMappingValue.pmsValue === row.pmsValue,
          );
        });
      }

      // Filter on search text and the active segment filter including all it's child segments
      const searchTextLowercase = this.searchText.toLocaleLowerCase();
      rowsFiltered = rowsFiltered.filter((row) => {
        if (this.benchmarkInsightBeniService.segmentFilter) {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          if (row.categoryId) {
            return (
              // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
              row.pmsValue
                .toLocaleLowerCase()
                // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                .startsWith(searchTextLowercase) &&
              // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
              row.categoryId.startsWith(
                this.benchmarkInsightBeniService.segmentFilter,
              )
            );
          }

          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          if (row.suggestedCategoryId) {
            return (
              // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
              row.pmsValue
                .toLocaleLowerCase()
                // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                .startsWith(searchTextLowercase) &&
              // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
              row.suggestedCategoryId.startsWith(
                this.benchmarkInsightBeniService.segmentFilter,
              )
            );
          }

          return false;
        }

        // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
        return row.pmsValue.toLocaleLowerCase().startsWith(searchTextLowercase);
      });

      // eslint-disable-next-line @typescript-eslint/no-unsafe-return
      return rowsFiltered;
    }

    return [];
  }

  // Determine state of the select all checkbox.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get selectAllCheckboxState() {
    let state;

    if (this.selectedRows.length && this.rowsFiltered.length > 0) {
      // Checked if all rows are selected and there are rows to select.
      state = SELECT_ALL_CHECKBOX_STATES.CHECKED;
    } else if (this.rowsFiltered.length > BULK_UPDATE_MAX_ROWS) {
      // Disabled if there are more rows than we can update.
      state = SELECT_ALL_CHECKBOX_STATES.DISABLED;
    } else {
      // Unchecked in all other cases.
      state = SELECT_ALL_CHECKBOX_STATES.UNCHECKED;
    }

    return state;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get selectedFilter() {
    const match = this.filterOptions.filter((filter) => {
      return filter?.id === this.currentFilter?.id;
    });
    if (match.length) {
      return match[0];
    }

    return this.defaultFilter;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get selectedRows() {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return this.rowsFiltered.filter(
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      (pmsMappingValue) => pmsMappingValue.isSelected,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get tableClassName() {
    if (this.selectedRows.length) {
      if (this.pmsMappingValuesToPatch.length) {
        return 'mapping-table__content--with-double-save-footer';
      }

      return 'mapping-table__content--with-save-footer';
    }

    return '';
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get unsavedOptionDisabled() {
    return this.pmsMappingValuesToPatch.length === 0;
  }

  constructor() {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line prefer-rest-params
    super(...arguments);

    // eslint-disable-next-line @typescript-eslint/unbound-method
    window.addEventListener('beforeunload', this.onBeforeUnload);
  }

  willDestroy(): void {
    super.willDestroy();

    // eslint-disable-next-line @typescript-eslint/unbound-method
    window.removeEventListener('beforeunload', this.onBeforeUnload);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  convertForApi() {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return this.pmsMappingValuesToPatch.map((pmsMappingValue) =>
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-return
      pmsMappingValue.toApiPatchFormat(),
    );
  }

  // @ts-expect-error: Incorrect type
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  createCategories(rootCategory) {
    // @ts-expect-error: Incorrect type
    const options = [];

    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    rootCategory.childrenSorted.forEach((category) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      const categoryName = category.getFullPathDisplayName(
        this.intl,
        this.errorTracking,
      );

      const categoryAsOption = {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        label: categoryName,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        value: categoryName,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
        id: category.id,
      };

      options.push(categoryAsOption);

      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      if (category.children.length) {
        const childrenOptions = this.createCategories(category);

        childrenOptions.forEach((childOption) => {
          options.push(childOption);
        });
      }
    });

    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return options;
  }

  discardChanges(): void {
    this.pmsMappingValuesToPatch = [];
    // @ts-expect-error: Incorrect type
    this.args.cancelMappingChanges();

    // @ts-expect-error: Incorrect type
    this.successfullyBulkMappedItems = 0;
  }

  markDismissedSuggestions(): void {
    this.pmsMappingValuesToPatch.forEach((pmsMappingValue) => {
      const { pmsValue } = pmsMappingValue;
      if (this.suggestionIsSelected !== undefined) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const previousList =
          JSON.parse(
            // @ts-expect-error: Incorrect type
            this.localStorage.getItem(DISMISSED_SUGGESTIONS_LOCALSTORAGE_KEY),
          ) ?? [];
        if (this.suggestionIsSelected) {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
          const newList = previousList.filter(
            // @ts-expect-error: Incorrect type
            (rejectedPmsValue) => rejectedPmsValue !== pmsValue,
          );
          this.localStorage.setItem(
            DISMISSED_SUGGESTIONS_LOCALSTORAGE_KEY,
            JSON.stringify(newList),
          );
        } else if (!this.suggestionIsSelected) {
          this.localStorage.setItem(
            DISMISSED_SUGGESTIONS_LOCALSTORAGE_KEY,
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            JSON.stringify([...previousList, pmsValue]),
          );
        }
      }
    });
  }

  // Sets isSelected property for all editable rows (PmsMappingValues) to the parameter 'selected'.
  // @ts-expect-error: Incorrect type
  selectAllRows(selected): void {
    this.rowsFiltered
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      .filter((pmsMappingValue) => !pmsMappingValue.readOnly)
      .forEach((pmsMappingValue) => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
        pmsMappingValue.isSelected = selected;
      });
  }

  setInitialValues(): void {
    this.rows.forEach((row) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const { pmsValue, categoryId } = row;
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      this.initialValues.push({ pmsValue, originalCategoryId: categoryId });
    });
  }

  // @ts-expect-error: Incorrect type
  updateOrAddToPatchArray(pmsMappingValue): void {
    const foundPmsMappingValue = this.pmsMappingValuesToPatch.find(
      (pmsMappingValueToPatch) =>
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        pmsMappingValueToPatch.pmsValue === pmsMappingValue.pmsValue,
    );
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    const originalPmsMappingValue = this.originalPmsMappingValues.find(
      // @ts-expect-error: Incorrect type
      (pmsMappingValueToPatch) =>
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        pmsMappingValueToPatch.pmsValue === pmsMappingValue.pmsValue,
    );

    /*
    If there is already a patch object in the array for the pmsMappingValue, replace it with the newer patch object. If the found object has not been changed then remove it.
    Otherwise add it to the array.
    */
    if (foundPmsMappingValue) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      if (originalPmsMappingValue?.categoryId === pmsMappingValue.categoryId) {
        const index =
          this.pmsMappingValuesToPatch.indexOf(foundPmsMappingValue);
        if (index !== -1) {
          this.pmsMappingValuesToPatch.splice(index, 1);
        }
      }
      const index = this.pmsMappingValuesToPatch.indexOf(foundPmsMappingValue);
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      this.pmsMappingValuesToPatch[index] = pmsMappingValue;
    } else {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      this.pmsMappingValuesToPatch = [
        // @ts-expect-error: Incorrect type
        ...this.pmsMappingValuesToPatch,
        // @ts-expect-error: Incorrect type
        pmsMappingValue,
      ];
    }
  }

  @action
  async confirmCancellation(): Promise<void> {
    const isConfirmed = await this.alerting.showConfirmWarningAlert({
      text: this.intl.t('benchmark_insight.mapping.confirm_unsaved_changes'),
    });

    if (isConfirmed) {
      this.discardChanges();
    }
  }

  @action
  // @ts-expect-error: Incorrect type
  handleFilter(event): void {
    this.showOnlyUnsavedChanges = false;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    this.currentFilter = event;
  }

  @action
  // @ts-expect-error: Incorrect type
  onBeforeUnload(event): void {
    if (this.args.isEditing) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      event.preventDefault();
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      event.returnValue = '';
    }
  }

  @action
  // @ts-expect-error: Incorrect type
  onChangeBulk(selectedCategory): void {
    // Update all rows category id and add them to patch array.
    this.selectedRows.forEach((pmsMappingValue) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
      pmsMappingValue.categoryId = selectedCategory.id;
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      pmsMappingValue.updateCategoryRef(this.args.rootCategory);

      this.updateOrAddToPatchArray(pmsMappingValue);
    });

    this.selectAllRows(false);
  }

  @action
  // @ts-expect-error: Incorrect type
  onChangeCategory(pmsMappingValue): void {
    // Update ref
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    pmsMappingValue.updateCategoryRef(this.args.rootCategory);

    // update whether suggestion is selected
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    if (pmsMappingValue.suggestedCategoryId !== null) {
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      this.suggestionIsSelected = pmsMappingValue.categoryId === null;
    }

    this.updateOrAddToPatchArray(pmsMappingValue);

    if (this.pmsMappingValuesToPatch.length) {
      // @ts-expect-error: Incorrect type
      this.args.startEditing();
    }

    // reset filter if no unsaved_changes left to show
    if (
      this.pmsMappingValuesToPatch.length === 0 &&
      this.currentFilter?.id === PMS_MAPPING_CATEGORY_FILTERS.UNSAVED_CHANGES
    ) {
      this.handleFilter(this.defaultFilter);
    }
  }

  @action
  // @ts-expect-error: Incorrect type
  onSearchTextInput(event): void {
    this.startSearch();
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
    this.searchText = event.target.value;
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    this.debouncedSearchTask.perform();

    // Unselect all rows.
    // This prevents some weird cases where you have some rows selected, then filter but the row is not visible anymore.
    this.selectAllRows(false);
  }

  @action
  // @ts-expect-error: Incorrect type
  onViewUnmappedInput(event): void {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
    this.isViewingUnmappedOnly = event.target.checked;
    this.benchmarkInsightBeniService.clearSegmentFilter();
  }

  @action
  saveChanges(): void {
    this.markDismissedSuggestions();
    const apiReady = this.convertForApi();
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    this.args.patchMapping.perform(apiReady);

    this.pmsMappingValuesToPatch = [];
    this.showOnlyUnsavedChanges = false;
    // @ts-expect-error: Incorrect type
    this.args.stopEditing();
  }

  @action
  selectAll(): void {
    if (this.selectAllCheckboxState === SELECT_ALL_CHECKBOX_STATES.UNCHECKED) {
      this.selectAllRows(true);
      // @ts-expect-error: Incorrect type
      this.args.startEditing();
    } else {
      this.selectAllRows(false);
      // @ts-expect-error: Incorrect type
      this.args.stopEditing();
    }
  }

  @action
  // @ts-expect-error: Incorrect type
  setRowSelection(row, isSelected): void {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
    row.isSelected = isSelected;
    if (this.selectedRows.length) {
      // @ts-expect-error: Incorrect type
      this.args.startEditing();
    } else {
      // @ts-expect-error: Incorrect type
      this.args.stopEditing();
    }
  }

  @action
  startSearch(): void {
    this.searchText = '';
  }

  @action
  toggleShowOnlyUnsavedChanges(): void {
    this.showOnlyUnsavedChanges = !this.showOnlyUnsavedChanges;
    if (this.showOnlyUnsavedChanges) {
      this.isViewingUnmappedOnly = false;
      this.currentFilter = {
        id: PMS_MAPPING_CATEGORY_FILTERS.UNSAVED_CHANGES,
        label: this.intl.t('settings.pms_mapping.filter_label.unsaved_changes'),
        // @ts-expect-error: Incorrect type
        disabled: this.unsavedOptionDisabled,
      };
    } else {
      // @ts-expect-error: Incorrect type
      this.currentFilter = null;
    }
  }

  <template>
    {{! @glint-nocheck }}
    {{#if this.pmsMappingValuesToPatch.length}}
      <PrismCard @contentLayout="noPadding" class="mapping-table">
        <:header as |Header|>
          <div class="flex items-center">
            {{! Left side of card title }}
            <Header.title @isDisabled={{@isDisabled}}>
              {{t "settings.pms_mapping.mapping"}}
            </Header.title>

            {{#if @rows}}
              <span
                class="text-200--regular ml-200
                  {{if @isDisabled 'mapping-table__item_count--disabled'}}"
              >
                {{t
                  "settings.pms_mapping.item_count"
                  count=this.rowsFiltered.length
                }}
              </span>
            {{/if}}
          </div>
        </:header>

        <:headerAfter>
          {{! Right side of card title }}
          <div
            class="mapping-table__options_container ic-product-tour-mapping-table-controls flex items-center justify-center"
          >
            <div class="mr-200">
              <PrismInput
                @autofocus={{true}}
                @iconAfter={{hash name="search"}}
                @isClearable={{true}}
                @onInput={{this.onSearchTextInput}}
                @placeholder={{t "general.search_placeholder"}}
                @removePadding={{true}}
              />
            </div>

            <PrismSelectInput
              @onChange={{this.handleFilter}}
              @options={{this.filterOptions}}
              @value={{this.selectedFilter}}
            />
          </div>
        </:headerAfter>

        <:content>
          <div
            class="mapping-table__content
              {{this.tableClassName}}
              {{if @isDisabled 'mapping-table__content--disabled'}}"
          >
            <PrismTable
              @columns={{this.columns}}
              @enableAutoSortsUpdate={{true}}
              @initialSorts={{this.initialSorts}}
              @removeTableBorder={{true}}
              @renderAll={{false}}
              @rows={{this.rows}}
            />

            {{#if this.selectedRows.length}}
              <div
                class="card-tile__footer mapping-table__footer mapping-table__footer--bulk_update flex flex-row"
              >
                {{t
                  "settings.pms_mapping.bulk_update.selected"
                  count=this.selectedRows.length
                }}

                <div class="ml-400">
                  <PrismSelectInput
                    @onChange={{this.onChangeBulk}}
                    @options={{this.categoryOptions}}
                    @placeholder={{t
                      "settings.pms_mapping.bulk_update.placeholder"
                    }}
                    @value={{null}}
                  />
                </div>
              </div>
            {{/if}}
          </div>
        </:content>

        <:footer>
          <div class="mapping-table__footer flex flex-row justify-between">
            <div
              class="unsaved-changes"
              role="button"
              {{on "click" this.toggleShowOnlyUnsavedChanges}}
            >
              {{t
                "benchmark_insight.mapping.unsaved_changes.count"
                count=this.pmsMappingValuesToPatch.length
              }}
            </div>

            <GeneralFormFieldsCancelAndConfirm
              @cancel={{this.confirmCancellation}}
              @cancelText={{t "benchmark_insight.mapping.discard_changes"}}
              @confirm={{this.saveChanges}}
              @confirmText={{t "benchmark_insight.mapping.save_changes"}}
              @isCancelDisabled={{@patchMapping.isRunning}}
              @isConfirmDisabled={{or
                @patchMapping.isRunning
                (not this.pmsMappingValuesToPatch.length)
              }}
              {{! Disable confirm if there are no rows to patch or while patching. }}
            />
          </div>
        </:footer>
      </PrismCard>
    {{else}}
      <PrismCard @contentLayout="noPadding" class="mapping-table">
        <:header as |Header|>
          <div class="flex items-center">
            {{! Left side of card title }}
            <Header.title @isDisabled={{@isDisabled}}>
              {{t "settings.pms_mapping.mapping"}}
            </Header.title>

            {{#if @rows}}
              <span
                class="text-200--regular ml-200
                  {{if @isDisabled 'mapping-table__item_count--disabled'}}"
              >
                {{t
                  "settings.pms_mapping.item_count"
                  count=this.rowsFiltered.length
                }}
              </span>
            {{/if}}
          </div>
        </:header>

        <:headerAfter>
          {{! Right side of card title }}
          <div
            class="mapping-table__options_container ic-product-tour-mapping-table-controls flex items-center justify-center"
          >
            <div class="mr-200">
              <PrismInput
                @autofocus={{true}}
                @iconAfter={{hash name="search"}}
                @isClearable={{true}}
                @onInput={{this.onSearchTextInput}}
                @placeholder={{t "general.search_placeholder"}}
                @removePadding={{true}}
              />
            </div>

            <PrismSelectInput
              @onChange={{this.handleFilter}}
              @options={{this.filterOptions}}
              @value={{this.selectedFilter}}
            />
          </div>
        </:headerAfter>

        <:content>
          <div
            class="mapping-table__content
              {{this.tableClassName}}
              {{if @isDisabled 'mapping-table__content--disabled'}}"
          >
            <PrismTable
              @columns={{this.columns}}
              @enableAutoSortsUpdate={{true}}
              @initialSorts={{this.initialSorts}}
              @removeTableBorder={{true}}
              @renderAll={{false}}
              @rows={{this.rows}}
            />

            {{#if this.selectedRows.length}}
              <div
                class="card-tile__footer mapping-table__footer mapping-table__footer--bulk_update flex flex-row"
              >
                {{t
                  "settings.pms_mapping.bulk_update.selected"
                  count=this.selectedRows.length
                }}

                <div class="ml-400">
                  <PrismSelectInput
                    @onChange={{this.onChangeBulk}}
                    @options={{this.categoryOptions}}
                    @placeholder={{t
                      "settings.pms_mapping.bulk_update.placeholder"
                    }}
                    @value={{null}}
                  />
                </div>
              </div>
            {{/if}}
          </div>
        </:content>
      </PrismCard>
    {{/if}}
  </template>
}
